import React, { useEffect, useContext, useState, useRef } from 'react';
import { store } from 'components/Store';
import EmptyMessage from 'components/Dashboard/components/EmptyMessage';
import StakeholderItem from './StakeholderItem';
import SkeletonHolderListSkeleton, { StakeholderSkeletonItem } from './StakeHolderListSkeleton';
import { useAuth0 } from '@auth0/auth0-react';
import WaitingAnimationScreen from './WaitingAnimationScreen';
import callCollectList from 'components/Dashboard/utilities/callCollectList';
import useNumberOfUnseenItems from 'components/Dashboard/hooks/useNumberOfUnseenItems';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import ChangeKeywordsCategory from 'components/Dashboard/utilities/changeKeywordsCategory';
import ChangeKeywordsExclusions from 'components/Dashboard/utilities/changeKeywordExclusions';
import useGetSectionFilters from 'components/Dashboard/Filters/useGetSectionFilters';
import ResearchPortalButton from 'components/Dashboard/components/ResearchPortalButton';
import parliamentarianUsers from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import idsWithoutImages from 'components/Dashboard/KeyUpdates/idsWithoutImages';
import ScrollbarList from 'components/Common/ScrollbarList';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import { createSectionName } from 'components/Dashboard/helpers/controlActiveFilters';
import useGetAccessToken from 'components/Dashboard/apiCalls/useGetAccessToken';
import { useFilters } from 'components/Dashboard/Filters/FiltersContext';
import OutsideFilters from 'components/Dashboard/Filters/ui/OutsideFilters';
import useAddMetaTags from 'components/Dashboard/utilities/addMetaTags';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';

const useCallContactIdsResults = () => {
  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { contactIdsCheck } = state;

  const setContactIdsCheck = (results) => {
    dispatch({
      type: 'MODIFY_SECTION',
      parameter: 'contactIdsCheck',
      value: results,
    });
  };

  return { setContactIdsCheck, contactIdsCheck };
};

const StakeHolderList = (props) => {
  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { search, referenceState, activePlan, contactIdsCheck, keywordsLists } = state;
  const { setShowStakeholdersCrmPopup } = props;
  const { contentResults, setContentResults } = useContentResults();

  const { contentFilters } = useFilters();
  const { enableReferenceSection, code } = activePlan;
  const { activeReference } = referenceState;
  const { query, activeSearch } = search;
  const [isLoading, setIsLoading] = useState(true);
  const [contactIdsResult, setContactIdsResult] = useState([]);
  const [currentlyProcessing, setCurrentlyProcessing] = useState({
    attempt: 0,
    state: false,
  });
  const [stakeholdersHash, setStakeholdersHash] = useState(null);
  const [preloadedImages, setPreloadedImages] = useState(new Set());

  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { generalApiCall } = useGeneralApiCall();
  const noUnmountedComponent = useRef(true);

  const topicIdQuery = getUrlParam('topic-id');
  const searchQuery = getUrlParam('search') || getUrlParam('or');
  const list = keywordsLists.find((item) => item.id === (topicIdQuery === 'all' ? null : parseInt(topicIdQuery)));
  const sectionName = createSectionName();
  const pageSize = parseInt(import.meta.env.VITE_APP_STAKEHOLDER_PAGE_SIZE);
  const { searchResultsSection, mainSectionTitle } = useMainSectionParameter(params);
  const scrollCondition = contentResults?.[sectionName]?.scrollPosition;
  let masterTopic = !!list && list?.id === null;
  let listConditional = !masterTopic && !activeReference && search?.list !== 'noListsCreated';
  const isMobile = useIsMobile(1280);
  const { getSectionFilters, callConditional } = useGetSectionFilters(sectionName);
  const { searchWithin, sort, filters, hash, sortMode, nationsFilter } = getSectionFilters();
  const { isAuthenticated } = useAuth0();
  const { getAccessToken } = useGetAccessToken();
  const { setContactIdsCheck } = useCallContactIdsResults();
  const { addMetaTags } = useAddMetaTags();

  const initialCall = useRef(null);
  initialCall.current = (source) => {
    setCurrentlyProcessing({ attempt: 0, state: false });
    const loadResults = async () => {
      try {
        await createResults(null, null, null, source);
      } catch (error) {}
    };
    if (callConditional) {
      if (!scrollCondition) {
        loadResults();
      } else {
        let title = `${mainSectionTitle}${
          mainSectionTitle !== '' || searchResultsSection ? sectionName : 'Stakeholder mapping'
        } - PolicyMogul`;
        if (location?.pathname === '/general-election-2024/candidates') {
          title = `Candidates - Election hub - PolicyMogul`;
        }
        addMetaTags({ title, hash: window.location.hash });
        setIsLoading(false);
        //AE: Necessary to have a setTimeOut to update the scrollPosition and allow continue using the list correctly. (Without this if you clear the search or change the topic it will not work.)
        setTimeout(() => {
          let StakeholdersWithoutScroll = {
            ...contentResults?.[sectionName],
            scrollPosition: null,
          };
          const nextContentResults = { ...contentResults, [sectionName]: StakeholdersWithoutScroll };
          setContentResults(nextContentResults);
        }, 300);
      }
    }
  };

  useEffect(() => {
    return () => {
      noUnmountedComponent.current = false;
    };
  }, []);

  useEffect(() => {
    const source = new AbortController();
    initialCall.current(source);
    return () => {
      source.abort('Stakeholders canceled by the user.');
    };
  }, [search, topicIdQuery, searchQuery, referenceState, contentFilters]);

  useEffect(() => {
    if (!isLoading && scrollCondition) {
      scrollBarRef.current.scrollTop(contentResults?.[sectionName].scrollPosition);
    }
  }, [isLoading, contentResults?.[sectionName]]);

  useEffect(() => {
    //UPDATE SELECTED STAKEHOLDERS STATE WHEN FILTERS ARE CHANGED
    dispatch({
      type: 'MODIFY_SECTION',
      parameter: 'selectedStakeholders',
      value: {
        selectedAll: false,
        selectedAllVisible: [],
        selectedIds: [],
      },
    });
  }, [topicIdQuery, stakeholdersHash]);

  useEffect(() => {
    //REMEMBER HASH FOR STAKEHOLDERS MAPPING
    if (location.hash !== stakeholdersHash) {
      setStakeholdersHash(location.hash);
    }
  }, [location.hash]);

  const checkReadyState = useRef(null);
  checkReadyState.current = (source) => {
    const callAgainResults = () => {
      setTimeout(async () => {
        try {
          await createResults(false, true, false, source);
        } catch (error) {
          console.error(error);
        }
      }, 2500);
    };
    if (currentlyProcessing.state) {
      setIsLoading(false);
      callAgainResults();
    }
  };

  useEffect(() => {
    const source = new AbortController();
    checkReadyState.current(source);
    return () => {
      if (currentlyProcessing.state) {
        source.abort('Stakeholders canceled by the user.');
      }
    };
  }, [currentlyProcessing]);

  const { numberOfUnseenItems } = useNumberOfUnseenItems();

  async function createResults(pageNumber, processing, getAll, sourceProp) {
    try {
      if (!pageNumber && !processing) {
        setIsLoading(true);
      }

      let token = isAuthenticated ? await getAccessToken() : '';
      let pathname = `/api/${listConditional && !!list ? 'stakeholder/query-ranked' : 'search/query'}`;
      let requestProperties = {
        type: 'stakeholder',
        pageSize: getAll ? 10000 : searchResultsSection && !enableReferenceSection ? 3 : pageSize,
        filters: filters,
        pageNumber: 1,
      };
      if (pageNumber) {
        requestProperties = { ...requestProperties, ...pageNumber };
      }
      if (sort) {
        requestProperties = { ...requestProperties, sort: sort };
      }

      if (listConditional && !!list) {
        requestProperties = {
          ...requestProperties,
          keywordListId: list?.id,
          includeExplanations: false,
        };

        if (nationsFilter) {
          let selectedOption = nationsFilter.options.find((item) => nationsFilter.optionSelected === item.name);
          requestProperties = {
            ...requestProperties,
            country: selectedOption.filterValue,
          };
        }
      } else {
        if (activeReference) {
          const { keywords, andKeywords, keywordExclusions } = referenceState;
          requestProperties = {
            ...requestProperties,
            query: ChangeKeywordsCategory(keywords),
            andQueries: ChangeKeywordsExclusions(andKeywords),
            mustNotQueries: ChangeKeywordsExclusions(keywordExclusions),
            smartMatch: false,
          };
        } else {
          requestProperties = {
            ...requestProperties,
            query: masterTopic || list === 'noListsCreated' ? '' : query,
            smartMatch: false,
          };
        }
        requestProperties = {
          ...requestProperties,
          includeHighlights: true,
        };
      }

      if (sectionName === 'Candidates') {
        if (searchQuery) {
          requestProperties.sort = null;
        }
      } else if (!(listConditional && !!list) || (searchWithin && !!list)) {
        requestProperties.sort = null;
      }

      if (listConditional) {
        requestProperties = {
          ...requestProperties,
          sortMode: sortMode ?? 'relevance',
        };
      }

      if (searchWithin && !!list) {
        if (list?.id !== null) {
          requestProperties = { ...requestProperties, ...searchWithin };
        } else {
          requestProperties = {
            ...requestProperties,
            query: searchWithin.searchWithinQuery,
          };
        }
      }

      //AE: EXCEPTION THAT ONLY APPLIES FOR STAKEHOLDERS FOR NOW
      if (masterTopic || list === 'noListsCreated') {
        requestProperties = {
          ...requestProperties,
          sort: { field: 'lastName', order: 'ASC' },
        };
      }

      let results = await generalApiCall({
        pathname,
        method: 'post',
        requestProperties,
        needsAuthentication: isAuthenticated,
        requestSource: sourceProp,
        notShowErrorMessage: true,
      });

      if (!!results) {
        if (getAll) {
          dispatch({
            type: 'MODIFY_SECTION',
            parameter: 'allStakeholders',
            value: results?.hits,
          });
        } else {
          let contactsIds = pageNumber ? contentResults?.[sectionName]?.contactsIds : [];
          let hitsResults = pageNumber ? contentResults?.[sectionName]?.hits : [];
          let highlightResults = pageNumber ? contentResults?.[sectionName]?.highlights : [];

          if (results?.hits !== null) {
            contactsIds = contactsIds?.concat(results.contactsIds);
            hitsResults = hitsResults?.concat(results.hits);
            highlightResults = highlightResults?.concat(results.highlights);
          }
          let currentId = results.hits !== null && results.hits.length > 0 ? results.hits[0].id : 0;
          let ResultsToState = {
            ...results,
            currentId: currentId,
            contactsIds: contactsIds,
            currentPositionId: contactsIds.indexOf(currentId),
            hits: hitsResults,
            highlights: highlightResults,
            thereIsMoreContent: results.hits !== null ? results.hits.length === pageSize : false,
          };

          let StakeHoldersResults = {
            ...contentResults,
            [sectionName]: ResultsToState,
          };
          if (!!list && list.id !== null) {
            setCurrentlyProcessing({
              attempt: currentlyProcessing.attempt + 1,
              state: results.currentlyProcessing,
            });
          }
          setContentResults(StakeHoldersResults);
          dispatch({
            type: 'MODIFY_SECTION',
            parameter: 'crmContactsTotalHits',
            value: results?.totalHits,
          });

          if (!pageNumber) {
            const imagesToPreload = [...results.hits].splice(0, 4);
            await loadImages({ items: imagesToPreload });
            setPreloadedImages(new Set(imagesToPreload.map((item) => item.imageId)));
          }

          let urlToHistory = `${location.pathname}${location.search}${hash !== '' ? `#${hash}` : ''}`;
          navigate(
            {
              search: window.location.search,
              hash: hash !== '' ? `#${hash}` : '',
            },
            { replace: true }
          );

          let newContactsIds = [
            ...contactIdsResult,
            ...results.hits.map((stakeholder) => stakeholder.id).filter((id) => !contactIdsResult.includes(id)),
          ];
          setContactIdsResult(newContactsIds);

          let title = `${mainSectionTitle}${
            mainSectionTitle !== '' || searchResultsSection
              ? sectionName
              : parliamentarianUsers(code)
                ? 'Policymakers'
                : 'Stakeholder mapping'
          } - PolicyMogul`;

          if (location?.pathname === '/general-election-2024/candidates') {
            title = `Candidates - Election hub - PolicyMogul`;
          }
          addMetaTags({ title, hash });
          setIsLoading(false);

          if (!pageNumber) {
            let contentType = 'Stakeholder';
            if (!!list && list !== 'noListsCreated' && isAuthenticated && !processing) {
              await callCollectList(contentType, token, list?.id, urlToHistory);
            }
            if (list !== 'noListsCreated') {
              await numberOfUnseenItems();
            }
          }
        }
      }
    } catch (error) {}
  }

  const scrollBarRef = useRef(null);

  const BlueBannerText = () => {
    return (
      <>
        {((searchResultsSection && referenceState.keywords.length > 0) || (activeSearch && list === null)) && (
          <p className='stakeholders-search-banner'>
            These results are based on names, positions and biographies only. To see all stakeholders interested in a
            topic, go to{' '}
            <Link
              to={`/people/stakeholder-mapping`}
              onClick={() => {
                dispatch({
                  type: 'MODIFY_SECTION',
                  parameter: 'search',
                  value: {
                    ...search,
                    query: '',
                    activeSearch: false,
                    list: null,
                  },
                });
                setContentResults({});
              }}
            >
              {parliamentarianUsers(code) ? 'policymakers' : 'Stakeholder mapping'}
            </Link>
          </p>
        )}
      </>
    );
  };
  const hitsToRender = contentResults?.[sectionName]?.hits ?? [];

  const stakeholderMapping =
    location.pathname.includes('/stakeholder-mapping') || location.pathname.includes('/policymakers');
  const stakeholderIds = contentResults?.[sectionName]?.hits?.map((item) => item.id);

  const loadImageUrl = (imageId) => {
    return new Promise((resolve, reject) => {
      const urlImage =
        imageId !== 0 && imageId !== null && !idsWithoutImages.includes(imageId)
          ? `https://www.parliamenttoday.com/image.php?imageid=${imageId}&ml=1`
          : `${import.meta.env.VITE_APP_CDNURL}/images/20210722-stakeholders-placeholder.png`;
      let base_image = new Image();
      base_image.src = urlImage;
      base_image.onload = () => {
        if (noUnmountedComponent.current) {
          resolve('success');
        }
      };
      base_image.onerror = () => {
        if (noUnmountedComponent.current) {
          resolve('error');
        }
      };
    });
  };

  const loadImages = async ({ items }) => {
    for (let i = 0; i < items.length; i++) {
      let item = items[i];
      if (noUnmountedComponent.current) {
        await loadImageUrl(item.imageId);
      } else {
        break;
      }
    }
  };

  return (
    <div className='row mx-0'>
      <div className={`flex-grow-1 ${stakeholderMapping ? 'reference-main-content' : ''}`}>
        {isLoading && (
          <>
            <SkeletonHolderListSkeleton stakeholderMapping={stakeholderMapping} />
          </>
        )}
        {!isLoading && (
          <div>
            {currentlyProcessing.state && <WaitingAnimationScreen waitingMessage={'the most relevant stakeholders'} />}
            {!currentlyProcessing.state && (
              <>
                <BlueBannerText />

                {hitsToRender?.length === 0 ? (
                  <div>
                    <EmptyMessage
                      resourceName={parliamentarianUsers(code) ? 'policymakers' : 'stakeholders'}
                      filtersFunction={createResults}
                    />
                  </div>
                ) : (
                  <>
                    <ScrollbarList
                      ref={scrollBarRef}
                      createResults={createResults}
                      additionalClass={stakeholderMapping ? 'stakeholder-list-scrollbar' : ''}
                    >
                      <ul className='policy-list px-0 items-list stakeholder-items-list'>
                        {hitsToRender?.map((item, index, array) => (
                          <StakeholderItem
                            array={array}
                            scrollBarRef={scrollBarRef}
                            item={item}
                            key={`item${item.id}`}
                            index={index}
                            stakeholderIds={stakeholderIds}
                            isContact={contactIdsCheck[item.id]}
                            setContactIdsCheck={setContactIdsCheck}
                            contactIdsCheck={contactIdsCheck}
                            setShowStakeholdersCrmPopup={setShowStakeholdersCrmPopup}
                            highlights={contentResults?.[sectionName]?.highlights?.[index]}
                            openInNewTab={!!location.pathname.includes('/general-election-2024')}
                            isPreloaded={preloadedImages.has(item.imageId)}
                          />
                        ))}
                        {contentResults?.[sectionName]?.thereIsMoreContent && <StakeholderSkeletonItem />}
                      </ul>
                      <ResearchPortalButton />
                    </ScrollbarList>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {stakeholderMapping && !isMobile && (
        <div className='ml-4 outside-filters-container '>
          <OutsideFilters section={'Stakeholders'} />
        </div>
      )}
    </div>
  );
};

export { useCallContactIdsResults };
export default StakeHolderList;
